import { useEffect, useMemo, useRef, useState } from 'react';
import type { PortalBootstrapData } from './types/window';
import { __ } from '@/hub-shared/utils/i18n';
import type { MissionDocumentRow } from '@tpct/mission-core/utils/missionStartApi';
import { useMissionSummary } from './hooks/useMissionSummary';
import { useMissionLifecycleActions } from './hooks/useMissionLifecycleActions';
import {
  missionApiConfigFromBootstrap,
  missionCanEditResponsable,
  missionIsReadOnly,
} from './types/missionPortal';
import MissionBreadcrumb from './components/mission/MissionBreadcrumb';
import MissionResponsableCard from './components/mission/MissionResponsableCard';
import MissionCollaboratorsCard from './components/mission/MissionCollaboratorsCard';
import MissionPreparationSection from './components/mission/MissionPreparationSection';
import MissionCyclesTable from './components/mission/MissionCyclesTable';
import MissionCompliancePanel from './components/mission/MissionCompliancePanel';
import MissionProgressBar from './components/mission/MissionProgressBar';
import MissionSignaturePanel from './components/mission/MissionSignaturePanel';
import { useMissionSignature } from './hooks/useMissionSignature';
import { vsmeMissionSetupRelativePath } from '@tpct/mission-core/utils/vsmePortalRoutes';
import {
  isMissionProgressActionAllowed,
  resolveApplicableProgressActionKey,
  resolveMissionActionStepKey,
  resolveMissionProgressPrimaryAction,
  resolveProgressBarStepKey,
  shouldShowMissionProgressAction,
} from './utils/missionLifecycleSteps';

type MissionPortalPageProps = {
  bootstrap: PortalBootstrapData;
  missionId: number;
  onNavigate: (path: string) => void;
};

const MissionPortalPage = ({ bootstrap, missionId, onNavigate }: MissionPortalPageProps) => {
  const {
    summary,
    loading,
    error,
    setError,
    refreshSummary,
    setSummary,
  } = useMissionSummary({
    hubBaseUrl: bootstrap.restUrls.accountingHub,
    nonce: bootstrap.nonce,
    missionId,
  });

  const { busyAction, applyLifecycleAction } = useMissionLifecycleActions({
    hubBaseUrl: bootstrap.restUrls.accountingHub,
    nonce: bootstrap.nonce,
    missionId,
    summary,
    onNavigate,
    refreshSummary,
    setSummary,
    setError,
  });

  // Per-collecte opt-in: whether the client company's designated signatory is invited alongside
  // the expert-comptable. Deliberately not persisted — the choice belongs to this signature run.
  const [includeCompanySignatory, setIncludeCompanySignatory] = useState(false);
  const [documents, setDocuments] = useState<MissionDocumentRow[]>([]);
  const [publishCatalog, setPublishCatalog] = useState(false);
  const [justSigned, setJustSigned] = useState(false);

  useEffect(() => {
    if (summary === null) {
      return;
    }
    setDocuments(summary.documents ?? []);
    setPublishCatalog(summary.publish_catalog === true);
  }, [summary]);

  const apiConfig = useMemo(
    () => missionApiConfigFromBootstrap(
      bootstrap.restUrls.accountingHub,
      bootstrap.restUrls.vsme,
      bootstrap.nonce,
    ),
    [bootstrap.nonce, bootstrap.restUrls.accountingHub, bootstrap.restUrls.vsme],
  );

  const signingAvailable = summary?.lifecycle?.signing_available === true;

  const progressStepKey = useMemo(() => {
    if (summary === null) {
      return resolveProgressBarStepKey(undefined, null, signingAvailable);
    }
    const storedStatus = summary.lifecycle?.status ?? summary.lifecycle?.stored_status ?? summary.mission.status;
    return summary.lifecycle?.progress_step_key
      ?? resolveProgressBarStepKey(storedStatus, summary.linked_report_id, signingAvailable);
  }, [signingAvailable, summary]);

  const actionStepKey = useMemo(() => {
    if (summary === null) {
      return null;
    }
    const storedStatus = summary.lifecycle?.status ?? summary.lifecycle?.stored_status ?? summary.mission.status;
    return resolveMissionActionStepKey(
      storedStatus,
      summary.linked_report_id,
      progressStepKey,
      signingAvailable,
    );
  }, [progressStepKey, signingAvailable, summary]);

  const signatureStepActive = signingAvailable
    && (progressStepKey === 'signature' || actionStepKey === 'signature');
  const {
    status: signatureStatus,
    loading: signatureLoading,
    busy: signatureBusy,
    error: signatureError,
    pollingTimedOut: signaturePollingTimedOut,
    syncing: signatureSyncing,
    relaunching: signatureRelaunching,
    relaunchFeedback: signatureRelaunchFeedback,
    syncNow: signatureSyncNow,
    relaunch: signatureRelaunch,
    cancelling: signatureCancelling,
    cancelFeedback: signatureCancelFeedback,
    cancel: signatureCancel,
    downloading: signatureDownloading,
    downloadError: signatureDownloadError,
    downloadDocument: signatureDownloadDocument,
    launchSignature,
    resetAndLaunch,
  } = useMissionSignature({
    hubBaseUrl: bootstrap.restUrls.accountingHub,
    nonce: bootstrap.nonce,
    missionId,
    enabled: !loading && summary !== null && signatureStepActive,
    // On promotion to signé the summary refresh flips the progress bar and drops the Signer button;
    // the page-level success notice confirms it since the signature card unmounts once signé.
    onCompleted: () => {
      setJustSigned(true);
      void refreshSummary();
    },
  });

  useEffect(() => {
    if (summary === null) {
      return;
    }

    const storedStatus = summary.lifecycle?.stored_status ?? summary.mission.status;
    if (storedStatus !== 'preparation') {
      return;
    }

    onNavigate(vsmeMissionSetupRelativePath(missionId));
  }, [missionId, onNavigate, summary]);

  useEffect(() => {
    const focus = new URLSearchParams(window.location.search).get('focus');
    if (focus !== 'signature' || summary === null) {
      return;
    }

    const progressCard = document.querySelector('.mission-page__card--signature');
    const target = progressCard instanceof HTMLElement
      ? progressCard
      : document.querySelector('.mission-page__card--span-5');
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [summary]);

  // Must be declared before early returns to satisfy rules-of-hooks
  const signaturePanelRef = useRef<HTMLDivElement>(null);

  if (loading) {
    return <p>{__('Chargement de la mission...')}</p>;
  }

  if (error !== '' && summary === null) {
    return (
      <div className="accounting-hub-notice" role="alert">
        <p>{error}</p>
      </div>
    );
  }

  if (summary === null) {
    return <p>{__('Mission introuvable.')}</p>;
  }

  const storedStatus = summary.lifecycle?.stored_status ?? summary.mission.status;
  if (storedStatus === 'preparation') {
    return <p>{__('Chargement de la mission...')}</p>;
  }

  const readOnly = missionIsReadOnly(summary);
  const progressPrimaryAction = actionStepKey !== null
    ? resolveMissionProgressPrimaryAction(actionStepKey)
    : null;
  const progressActionKey = actionStepKey !== null
    ? resolveApplicableProgressActionKey(actionStepKey, summary.lifecycle?.allowed_actions)
    : null;
  const showProgressAction = shouldShowMissionProgressAction(
    actionStepKey,
    readOnly,
    summary.lifecycle?.status ?? summary.lifecycle?.stored_status ?? summary.mission.status,
  );
  const progressActionAllowed = isMissionProgressActionAllowed(
    progressActionKey,
    summary.lifecycle?.allowed_actions,
  );

  // Stage detection drives the single Signer button. An in-progress collecte is NOT automatically a
  // wedged one: while JeSignExpert still holds a live signing URL the ceremony can simply be resumed,
  // and resetting would cancel a signature that may already be valid — on 20 August 2026 a completed
  // signature was shown as "en cours" with relaunch as the only offered action. Reset-and-relaunch
  // from this button stays reachable, but only once there is nothing left to resume. Cancelling
  // without relaunching is the panel's "Annuler la signature", after which this button is Signer.
  // Without the `signatory` right on the connected office the ceremony could never apply the
  // signature, so the button is blocked.
  const isSignAction = progressActionKey === 'sign';
  const collectionInFlight = isSignAction && ( signatureStatus?.collection?.state ?? '' ) === 'inProgress';
  const resumeSigningUrl = signatureStatus?.can_resume_signing === true ? signatureStatus.signing_url ?? '' : '';
  const canResumeSigning = collectionInFlight && resumeSigningUrl !== '';
  const signatureStuck = collectionInFlight && !canResumeSigning;
  const signBlocked = isSignAction && signatureStatus !== null && signatureStatus.can_sign === false;

  const handleProgressAction = async (): Promise<void> => {
    if (progressActionKey === null) {
      return;
    }

    if (progressActionKey === 'sign') {
      const signingUrl = signatureStuck
        ? await resetAndLaunch()
        : await launchSignature(includeCompanySignatory);
      if (signingUrl !== null && signingUrl !== '') {
        window.open(signingUrl, '_blank', 'noopener,noreferrer');
      } else {
        // can_start was false — panel shows the correct step; scroll to it
        signaturePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      await refreshSummary();
      return;
    }

    await applyLifecycleAction(progressActionKey);
  };

  // onNavigate prepends portalPath, so this must stay relative like all other route helpers.
  const connectionsPath = '/connections';
  const signActionBusy = progressActionKey === 'sign' ? signatureBusy : busyAction === progressActionKey;
  const signButtonLabel = canResumeSigning
    ? __('Reprendre la signature')
    : signatureStuck
      ? __('Relancer la signature')
      : progressPrimaryAction?.label ?? '';

  return (
    <div className="mission-page">
      <MissionBreadcrumb missionTitle={summary.mission.title} onNavigate={onNavigate} />

      {error !== '' ? (
        <div className="accounting-hub-notice accounting-hub-notice-error" role="alert">
          <p>{error}</p>
        </div>
      ) : null}

      <div className="mission-page__grid">
        <div className="container-style mission-page__card mission-page__card--span-5">
          <h2 className="h4">{__('Avancement de la mission')}</h2>
          <MissionProgressBar
            currentStepKey={progressStepKey}
            signingAvailable={signingAvailable}
          />
          {justSigned ? (
            <div className="accounting-hub-notice accounting-hub-notice-success" role="status">
              <p>{__('Signature effectuée. Le rapport signé est disponible au téléchargement.')}</p>
            </div>
          ) : null}
          {showProgressAction && progressPrimaryAction !== null && progressActionKey !== null ? (
            <div className="mission-page__card-actions">
              <button
                type="button"
                className="btn-primary"
                disabled={signActionBusy || !progressActionAllowed || signBlocked}
                onClick={() => {
                  // Resuming opens the live ceremony synchronously, inside the click: no await
                  // first, so the browser still sees a user gesture and does not block the tab.
                  if (canResumeSigning) {
                    window.open(resumeSigningUrl, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  void handleProgressAction();
                }}
              >
                {signActionBusy
                  ? __('Chargement...')
                  : ( isSignAction ? signButtonLabel : progressPrimaryAction.label )}
              </button>
            </div>
          ) : null}
        </div>

        {signatureStepActive ? (
          <div ref={signaturePanelRef} className="container-style mission-page__card mission-page__card--signature mission-page__card--span-5">
            <h2 className="h4">{__('Signature électronique')}</h2>
            <MissionSignaturePanel
              status={signatureStatus}
              loading={signatureLoading}
              error={signatureError}
              connectionsPath={connectionsPath}
              pollingTimedOut={signaturePollingTimedOut}
              syncing={signatureSyncing}
              relaunching={signatureRelaunching}
              relaunchFeedback={signatureRelaunchFeedback}
              cancelling={signatureCancelling}
              cancelFeedback={signatureCancelFeedback}
              downloading={signatureDownloading}
              downloadError={signatureDownloadError}
              includeCompanySignatory={includeCompanySignatory}
              onToggleCompanySignatory={setIncludeCompanySignatory}
              onSyncNow={() => { void signatureSyncNow(); }}
              onRelaunch={() => { void signatureRelaunch(); }}
              onCancel={() => { void signatureCancel(); }}
              onDownload={(kind) => { void signatureDownloadDocument(kind); }}
              onNavigate={onNavigate}
            />
          </div>
        ) : null}

        <div className="mission-page__sidebar">
          <MissionResponsableCard
            apiConfig={apiConfig}
            missionId={missionId}
            displayName={summary.mission.responsable_display_name}
            responsableUserId={summary.mission.responsable_user_id}
            readOnly={readOnly || !missionCanEditResponsable(summary)}
            currentUserId={bootstrap.currentUser.id}
            portalPath={bootstrap.portalPath}
            onNavigate={onNavigate}
            onUpdated={refreshSummary}
            onError={setError}
          />
          <MissionCollaboratorsCard
            collaborators={summary.collaborators ?? []}
            currentUserId={bootstrap.currentUser.id}
            portalPath={bootstrap.portalPath}
            onNavigate={onNavigate}
          />
        </div>

        <MissionPreparationSection
          apiConfig={apiConfig}
          missionId={missionId}
          documents={documents}
          requiredDocumentTypes={summary.required_document_types ?? []}
          readOnly={readOnly}
          onDocumentsChange={setDocuments}
          onError={setError}
        />

        <MissionCyclesTable
          vsmeBaseUrl={bootstrap.restUrls.vsme}
          nonce={bootstrap.nonce}
          linkedReportId={summary.linked_report_id}
          missionId={missionId}
          companyId={summary.mission.company_id}
          onNavigate={onNavigate}
        />

        <MissionCompliancePanel
          apiConfig={apiConfig}
          missionId={missionId}
          publishCatalog={publishCatalog}
          readOnly={readOnly}
          onPublishCatalogChange={setPublishCatalog}
          onError={setError}
          onUpdated={refreshSummary}
        />
      </div>
    </div>
  );
};

export default MissionPortalPage;
